import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
import {
  IsColumnFuncParams,
  ValueFormatterParams,
} from "ag-grid-community/dist/lib/entities/colDef";
import {
  BfxConfigService,
  BfxSysConfigUtilService,
  SysFeatures,
  SystemConfigPromiseService,
} from "shared/auth-services";
import {
  AlgoStrategyLookupService,
  BaseTableDefinitionFactory,
  CcyLookupService,
  CcyPairLookupService,
  ClientLookupService,
  ColumnFilterType,
  CurrencyPairCalendarLookupService,
  IndexType,
  ProfilePromiseActionService,
  SalesGroupLookupService,
  UserLookupService,
} from "shared/cubiq-services";
import { ValueDateHelper } from "shared/cubiq-services/model/orders/ValueDateHelper";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { COLUMN_SELECTION } from "shared/cubiq-services/model/TableDefinitionFactory.constants";
import { BfxConfigIDRefHelper } from "shared/helper-services/bfx-config-id-ref-helper";
import { FwdRollMutableRowModel } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/FwdRollMutableRowModel";

const logger = LoggerService.for(__dirname);

const CLASSES_EDITABLE_PTS = (params) => {
  if (!params.data?.spotAggregateFg) {
    const editable = params?.node?.data?.isEditable;
    if (!editable) {
      return "blotter_amt_align fwd_roll_highlight rate_readonly";
    }
    return "blotter_amt_align fwd_roll_highlight";
  }
  return "blotter_amt_align";
};

const CLASSES_EDITABLE_MARKUP = (params) => {
  if (!params.data?.spotAggregateFg) {
    const editable = params?.node?.data?.isEditable && params?.node?.data?.isMarkupEditable;
    if (!editable) {
      return "blotter_amt_align fwd_roll_highlight rate_readonly";
    }
    return "blotter_amt_align fwd_roll_highlight";
  }
  return "blotter_amt_align";
};

const CLASS_RULES_MARKET_PTS = {
  "updated-price": (params) => params.data?.isMarketPtsChanged,
  fwd_roll_missing_pts: (params) =>
    params.data?.missingFwdPoints === true && !params.data?.isMarketPtsChanged,
  fwd_roll_hidden: (params) => params.data?.hidden === true,
};

const CLASS_RULES_AGREED_FWD_PTS = {
  fwd_roll_failed_adjustment: (params) => IS_FF_FAILED_ADJUSTMENT(params),
};

const CLASS_RULES_CLIENT_PTS = {
  "updated-price": (params) => params.data?.isClientPtsChanged,
};
const CLASS_RULES_FWD_MARKUP = {
  "updated-price": (params) => params.data?.isFwdMarkupChanged,
};

const IS_FF = (params) => {
  return params.data?.forwardFirstOrder === true;
};

const IS_FF_FAILED_ADJUSTMENT = (params) => {
  return params.data?.failedAdjCalculation === true;
};

const IS_SPOT = (params) => {
  if (params.data) {
    return params.data.spotAggregateFg || params.data.settleTenor === CubiqTS.Tenor.SPOT;
  }
  return true;
};

const IS_SPOT_AGGREGATE = (params) => {
  if (params.data) {
    return params.data.spotAggregateFg;
  }
  return true;
};

const IS_HIDDEN = (params) => {
  if (params.data) {
    // Hidden is only defined on CCy Model
    return params.data?.hidden;
  }
  return false;
};

const ONE = new CubiqTS.BigNumber("1");
const ONE_HUNDRED = new CubiqTS.BigNumber("100");

export const COL_AGREED_PTS = "reqForwardPts";
export const COL_ADJUSTED_PTS_PERCENT_CHANGE = "_apcForwardPts";

export abstract class FwdRollBaseTableDefinitionFactory extends BaseTableDefinitionFactory {
  protected defaultSortModel: any = [{ colId: "creationDate", sort: "desc" }];

  protected valueDateHelper: ValueDateHelper;
  protected partFillDisabledTypeText: string = "";
  protected privateCommentsEnabled: boolean;
  protected enabledOBO: boolean;

  constructor(
    profileService: ProfilePromiseActionService,
    protected _ccyPairLookupService: CcyPairLookupService,
    protected _ccyLookupService: CcyLookupService,
    protected _sysConfigService: SystemConfigPromiseService,
    protected clientLookup: ClientLookupService,
    protected userLookupService: UserLookupService,
    protected salesGroupLookup: SalesGroupLookupService,
    protected currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
    protected algoStrategyLookupService: AlgoStrategyLookupService,
    protected sysConfigUtil: BfxSysConfigUtilService,
    protected configService: BfxConfigService,
  ) {
    super(profileService);

    this.valueDateHelper = new ValueDateHelper(currencyPairCalendarLookupService);

    configService.getUIConfig().then((config) => {
      this.enabledOBO = config && config.general ? config.general.oboEnabled : false;
    });
  }

  protected fwdIsEditable(params: IsColumnFuncParams): boolean {
    return params?.node?.data?.isEditable;
  }

  protected fwdMarkupIsEditable(params: IsColumnFuncParams): boolean {
    return params?.node?.data?.isEditable && params?.node?.data?.isMarkupEditable;
  }

  protected createSalesGroupColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Sales Group";
    col.field = "salesGroupId";
    col.minWidth = this.oneWs;
    col.width = this.sevenWs;
    col.columnFilterType = ColumnFilterType.SALESGROUP;
    col.valueGetter = (params) => {
      const sg = this.salesGroupLookup.getSalesGroup(params.data.salesGroupId);
      return sg?.groupCode ?? "";
    };

    if (!this.restoreFromConfig(col)) {
      col.hide = true;
      col.position = this.position.next();
    }
    return col;
  }

  protected createUSDEquivColumn() {
    const col: BfxColDef = { suppressMenu: true };
    col.headerName = "Order Amt USD";
    col.field = "usdEquivAmt";
    col.indexType = IndexType.NaturalString;
    col.minWidth = this.oneWs;
    col.width = this.eightWs;
    col.cellClass = ["blotter_amt_align"];
    col.valueFormatter = this.formatUSDAmt.bind(this);
    col.columnFilterType = ColumnFilterType.NUMBER;
    col.columnFilterData = null;
    col.valueGetter = (params) => {
      return params.data.usdEquivAmt;
    };

    if (!this.restoreFromConfig(col)) {
      col.hide = true;
      col.position = this.position.next();
    }
    return col;
  }

  protected createOBOAccountColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "OBO Account";
    col.field = "oboAccount";
    col.minWidth = this.oneWs;
    col.width = this.sevenWs;
    col.columnFilterType = ColumnFilterType.NONE;
    col.valueFormatter = (params: ValueFormatterParams) => {
      return params.value == null ? "-" : params.value;
    };

    if (!this.restoreFromConfig(col)) {
      col.hide = true;
      col.position = this.position.next();
    }
    return col;
  }

  protected createOBOClientColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "OBO Client";
    col.field = "oboClient";
    col.minWidth = this.oneWs;
    col.width = this.sevenWs;
    col.columnFilterType = ColumnFilterType.NONE;
    col.valueFormatter = (params: ValueFormatterParams) => {
      return params.value == null ? "-" : params.value;
    };

    if (!this.restoreFromConfig(col)) {
      col.hide = true;
      col.position = this.position.next();
    }
    return col;
  }

  protected createEntryDateColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.field = "entryDts";
    col.headerName = "Entry Date";
    col.minWidth = this.oneWs;
    col.width = this.nineWs;
    col.indexType = IndexType.Double;
    col.disableInPicker = false;
    col.valueFormatter = this.formatMomment.bind(this);
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = true;
    }
    return col;
  }

  protected createEntryUserColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Entry User";
    col.field = "entryUserCode";
    col.minWidth = this.oneWs;
    col.width = this.sixWs;
    col.columnFilterType = ColumnFilterType.USER;
    col.valueGetter = (params) => {
      let user = this.userLookupService.getUser(params.data.entryUserCode);
      return user;
    };
    col.valueFormatter = (params) => {
      let user = params.value;
      if (user) {
        return this.showUserName ? user.userName : user.userCode;
      }
      return "";
    };

    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = true;
    }
    return col;
  }

  protected createAllocationIDColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.field = "allocationId";
    col.headerName = "Allocation ID";
    col.indexType = IndexType.Integer;
    col.minWidth = this.oneWs;
    col.width = this.sevenWs;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = true;
    }
    return col;
  }

  protected createOrderIDColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.field = "orderId";
    col.headerName = "Order ID";
    col.indexType = IndexType.Integer;
    col.minWidth = this.oneWs;
    col.width = BfxConfigIDRefHelper.getOrderRefWs();
    col.valueFormatter = BfxConfigIDRefHelper.orderRefValueFormatter;
    col.getQuickFilterText = BfxConfigIDRefHelper.orderRefValueFormatter;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = true;
    }
    return col;
  }

  protected createChannelColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Channel";
    col.field = "channelCd";
    col.minWidth = this.oneWs;
    col.width = this.fiveWs;
    col.columnFilterType = ColumnFilterType.CHANNEL;
    if (!this.restoreFromConfig(col)) {
      col.hide = true;
      col.position = this.position.next();
    }
    col.valueFormatter = (params) => {
      let displayName: string;
      if (params.value) {
        displayName = this.clientLookup.getTradingChannelProfileDisplayName(params.value);
      }

      return displayName ?? "";
    };
    return col;
  }

  protected createForwardMarkupColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.valueFormatter = this.formatPoints.bind(this);
    col.field = "fwdMarkup";
    col.headerName = "Fwd Markup";
    col.disableInPicker = true;
    col.minWidth = this.fourWs;
    col.width = this.fiveWs;
    col.editable = this.fwdMarkupIsEditable;
    col.cellEditor = "fwdMarkupSpinner";
    col.cellClass = CLASSES_EDITABLE_MARKUP;
    col.cellClassRules = CLASS_RULES_FWD_MARKUP;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    col.valueGetter = (params) => {
      return IS_SPOT(params) ? null : params.data.forwardMarkupScaled;
    };
    col.valueSetter = (params) => {
      return params.data?.updateForwardMarkup(params.newValue);
    };
    col.hide = false;
    return col;
  }

  protected createClientForwardRateColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.field = "clientForwardRate";
    col.headerName = "Client All-In";
    col.valueFormatter = this.formatRate.bind(this);
    col.minWidth = this.sixWs;
    col.width = this.sevenWs;
    col.disableInPicker = false;
    col.editable = false;
    col.cellClass = "blotter_amt_align";
    col.valueGetter = (params) => {
      return IS_SPOT_AGGREGATE(params) ? null : params.data.clientForwardRate;
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    col.hide = false;
    return col;
  }

  protected createClientForwardPtsColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.field = "clientForwardPts";
    col.headerName = "Client Pts";
    col.valueFormatter = this.formatPoints.bind(this);
    col.minWidth = this.fourWs;
    col.width = this.fiveWs;
    col.disableInPicker = true;
    col.editable = this.fwdIsEditable;
    col.cellEditor = "clientPtsSpinner";
    col.cellClass = CLASSES_EDITABLE_PTS;
    col.cellClassRules = CLASS_RULES_CLIENT_PTS;

    col.valueGetter = (params) => {
      return IS_SPOT(params) ? null : params.data.clientForwardPtsScaled;
    };
    col.valueSetter = (params) => {
      return params.data?.updateClientForwardPts(params.newValue);
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    col.hide = false;
    return col;
  }

  protected createClientSpotColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.field = "clientSpotRate";
    col.headerName = "Client Spot";
    col.valueFormatter = this.formatRate.bind(this);
    col.minWidth = this.sixWs;
    col.width = this.sevenWs;
    col.disableInPicker = false;
    col.editable = false;
    col.cellClass = "blotter_amt_align";
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    col.hide = false;
    return col;
  }

  protected createSpotMarkupColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.valueFormatter = this.formatSpotMarkup.bind(this);
    col.field = "spotMarkup";
    col.headerName = "Spot Markup";
    col.disableInPicker = false;
    col.minWidth = this.fourWs;
    col.width = this.fiveWs;
    col.cellClass = "blotter_amt_align";
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    col.hide = false;
    return col;
  }

  protected createMarketForwardRateColumn(isMid: boolean) {
    let col: BfxColDef = { suppressMenu: true };
    if (isMid) {
      col.headerName = "Market Mid All-In";
    } else {
      col.headerName = "Market All-In";
    }
    col.field = "marketForwardRate";
    col.minWidth = this.fourWs;
    col.width = this.fiveWs;
    col.disableInPicker = false;
    col.editable = false;
    col.cellClass = "blotter_amt_align";
    col.valueGetter = (params) => {
      return IS_SPOT_AGGREGATE(params) ? null : params.data.marketForwardRate;
    };
    col.valueFormatter = this.formatRate.bind(this);
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    col.hide = false;
    return col;
  }

  protected createMarketForwardPtsColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.headerName = "Market Pts";
    col.field = "marketForwardPts";
    col.indexType = IndexType.NaturalString;
    col.minWidth = this.fourWs;
    col.width = this.fiveWs;
    col.disableInPicker = true;
    col.editable = this.fwdIsEditable;
    col.cellEditor = "marketPtsSpinner";
    col.cellClass = CLASSES_EDITABLE_PTS;
    col.cellClassRules = CLASS_RULES_MARKET_PTS;

    col.valueFormatter = this.formatPoints.bind(this);
    col.valueGetter = (params) => {
      return IS_SPOT(params) ? null : params.data.marketForwardPtsScaled;
    };
    col.valueSetter = (params) => {
      return params.data?.updateMarketForwardPts(params.newValue);
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    col.hide = false;
    return col;
  }

  protected createAgreedForwardPtsColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.headerName = "Agreed Pts";
    col.field = COL_AGREED_PTS;
    col.indexType = IndexType.NaturalString;
    col.minWidth = this.fourWs;
    col.width = this.fiveWs;
    col.disableInPicker = true;
    col.editable = false;
    col.cellClassRules = CLASS_RULES_AGREED_FWD_PTS;

    col.valueFormatter = this.formatPoints.bind(this);
    col.valueGetter = (params) => {
      return IS_FF(params) ? params.data.reqForwardPtsScaled : null;
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    col.hide = false;
    return col;
  }

  protected createAgreedPercentChangeColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.headerName = "Pts Δ %";
    col.headerTooltip = "Percent Change between Adjusted Market Pts and Agreed Pts";
    col.field = COL_ADJUSTED_PTS_PERCENT_CHANGE;
    col.indexType = IndexType.NaturalString;
    col.minWidth = this.fourWs;
    col.width = this.fiveWs;
    col.editable = false;
    col.cellClassRules = CLASS_RULES_AGREED_FWD_PTS;

    col.valueFormatter = (params) => {
      if (IS_SPOT(params) || IS_HIDDEN(params) || !IS_FF(params)) {
        return "";
      }

      if (CubiqTS.BigNumber.isBigNumber(params.value) && !params.value.isNaN()) {
        return `${params.value.toFixed(2)}%`;
      }

      return "-";
    };

    col.valueGetter = (params) => {
      if (
        IS_FF(params) &&
        CubiqTS.BigNumber.isBigNumber(params.data?.marketForwardPtsScaled) &&
        CubiqTS.BigNumber.isBigNumber(params.data?.reqForwardPtsScaled) &&
        !params.data.reqForwardPts.isZero()
      ) {
        const pair = this._ccyPairLookupService.getCcyPair(
          params.data.baseCcy,
          params.data.nonBaseCcy,
        );
        const precision =
          pair.base === params.data.nonBaseCcy
            ? pair.fwdInvertDecimalsQty
            : pair.fwdRateDecimalsQty;
        const BigNumber = CubiqTS.BigNumber.clone({
          DECIMAL_PLACES: precision,
          ROUNDING_MODE: CubiqTS.BigNumber.ROUND_HALF_UP,
        });

        try {
          const change = ONE_HUNDRED.multipliedBy(
            ONE.minus(
              new BigNumber(params.data.marketForwardPtsScaled).dividedBy(
                new BigNumber(params.data.reqForwardPtsScaled),
              ),
            ),
          ).abs();
          return change;
        } catch (e) {
          null;
        }
      }
      return null;
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    col.hide = false;
    return col;
  }

  protected createMarketSpotColumn(isMid: boolean) {
    let col: BfxColDef = { suppressMenu: true };
    col.field = "marketSpotRate";
    if (isMid) {
      col.headerName = "Market Mid Spot";
    } else {
      col.headerName = "Market Spot";
    }
    col.indexType = IndexType.NaturalString;
    col.valueFormatter = this.formatRate.bind(this);
    col.minWidth = this.sixWs;
    col.width = this.sevenWs;
    col.disableInPicker = false;
    col.editable = false;
    col.cellClass = "blotter_amt_align";
    col.valueGetter = (params) => {
      return params.data.marketSpotRate;
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    col.hide = false;
    return col;
  }

  protected createDealtAmtColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.field = "fixedAmt";
    col.headerName = "Dealt Amt";
    col.minWidth = this.fourWs;
    col.width = this.sevenWs;
    col.valueFormatter = this.formatFixedAmt.bind(this);
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    col.cellClass = "blotter_amt_align";
    return col;
  }

  protected createCounterAmtColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.field = "counterAmt";
    col.headerName = "Counter Amt";
    col.minWidth = this.fourWs;
    col.width = this.sevenWs;
    col.valueFormatter = this.formatCounterAmt.bind(this);
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    col.cellClass = "blotter_amt_align";
    return col;
  }

  protected createValueDateColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.field = "settleDate";
    col.headerName = "Value Date";
    col.cellClass = "blotter_left_align_col";
    col.minWidth = this.oneWs;
    col.width = this.nineWs;
    col.disableInPicker = true;
    col.indexType = IndexType.Double;
    col.valueGetter = (params) => {
      return this.valueDateHelper.getMoment(params.data);
    };
    col.valueFormatter = (params) => {
      if (!params.data?.spotAggregateFg && params.value) {
        return this.valueDateHelper.formatValueDate(params.value, params.data.settleTenor);
      }
      return "";
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    return col;
  }

  protected createIntentColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.headerName = "Dir";
    col.field = "clientIntent";
    col.indexType = IndexType.String;
    col.minWidth = this.twoWs;
    col.width = this.threeWs;
    col.cellRenderer = "intentRenderer";
    col.cellClass = ["blotter_center_align_col intent_col"];
    col.columnFilterType = ColumnFilterType.NONE;
    col.columnFilterData = CubiqTS.OrderIntent;
    col.valueGetter = (params) => {
      switch (params.data.clientIntent) {
        case CubiqTS.OrderIntent.BUY:
          return "BUY";
        case CubiqTS.OrderIntent.SELL:
          return "SELL";
      }
      return "";
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    return col;
  }

  protected createDealtCcyColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Dealt";
    col.field = "fixedCcy";
    col.indexType = IndexType.String;
    col.minWidth = this.oneWs;
    col.width = this.threeWs;
    col.omitFromPicker = false;
    col.columnFilterType = ColumnFilterType.CURRENCY;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    return col;
  }

  protected createInstrumentColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Instrument";
    col.field = "instrument";
    col.indexType = IndexType.String;
    col.minWidth = this.oneWs;
    col.width = this.fourWs + 4;
    col.omitFromPicker = false;
    col.columnFilterType = ColumnFilterType.CURRENCY_PAIR;
    col.valueGetter = (params) => {
      return params.data.baseCcy + "/" + params.data.nonBaseCcy;
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    return col;
  }

  protected createAccountNameColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Account";
    col.field = "accountName";
    col.indexType = IndexType.String;
    col.minWidth = this.oneWs;
    col.width = this.sixWs;
    col.columnFilterType = ColumnFilterType.ACCOUNT;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    return col;
  }

  // ccy
  protected createNetCcyColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Ccy";
    col.field = "fixedCcy";
    col.indexType = IndexType.String;
    col.minWidth = this.oneWs;
    col.width = this.threeWs;
    col.columnFilterType = ColumnFilterType.NONE;
    col.disableInPicker = true;
    col.valueFormatter = (params) => {
      return params.data?.spotAggregateFg ? params.data.fixedCcy : "";
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    return col;
  }
  protected createNetInstrumentColumn() {
    let col: BfxColDef = this.createInstrumentColumn();
    col.disableInPicker = true;
    col.valueFormatter = (params) => {
      return params.data?.spotAggregateFg ? params.data.baseCcy + "/" + params.data.nonBaseCcy : "";
    };
    return col;
  }

  // direction
  protected createNetIntentColumn() {
    let col: BfxColDef = { suppressMenu: true };

    col.headerName = "Dir";
    col.field = "netIntent";
    col.indexType = IndexType.String;
    col.minWidth = this.oneWs;
    col.width = this.threeWs;
    // col.cellRenderer = "intentRenderer";
    col.cellClass = ["blotter_center_align_col"];
    col.columnFilterType = ColumnFilterType.NONE;
    col.columnFilterData = CubiqTS.OrderIntent;
    col.valueGetter = (params) => {
      switch (params.data.netIntent) {
        case CubiqTS.OrderIntent.BUY:
          return "BUY";
        case CubiqTS.OrderIntent.SELL:
          return "SELL";
      }
      return "";
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    return col;
  }

  protected createNetFixedAmtColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Position Amt";
    col.field = "netFixedAmt";
    col.indexType = IndexType.String;
    col.minWidth = this.sixWs;
    col.width = this.nineWs;
    col.valueFormatter = this.formatFixedAmt.bind(this);
    col.cellClass = "blotter_amt_align";
    col.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    return col;
  }

  protected createNetCounterAmtColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Counter Amt";
    col.field = "netCounterAmt";
    col.indexType = IndexType.String;
    col.minWidth = this.sixWs;
    col.width = this.nineWs;
    col.valueFormatter = this.formatCounterAmt.bind(this);
    col.cellClass = "blotter_amt_align";
    col.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    return col;
  }

  protected createNetUSDEquivAmtColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "USD Equiv.";
    col.field = "netUsdEquivAmt";
    col.indexType = IndexType.String;
    col.minWidth = this.sixWs;
    col.width = this.nineWs;
    col.valueFormatter = this.formatUSDAmt.bind(this);
    col.cellClass = "blotter_amt_align";
    col.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    return col;
  }

  protected createOrderCountColumn() {
    let col: BfxColDef = { suppressMenu: true };
    col.headerName = "Orders";
    col.field = "orderCount";
    col.indexType = IndexType.Integer;
    col.minWidth = this.twoWs;
    col.width = this.fourWs;
    col.columnFilterType = ColumnFilterType.NONE;
    col.omitFromPicker = false;
    col.cellClass = "blotter_amt_align";
    col.valueGetter = (params) => {
      return params.data.orderCount ?? "-";
    };
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
      col.hide = false;
    }
    return col;
  }

  protected createOrderStatusColumn() {
    const statusCol: BfxColDef = { suppressMenu: true };

    // field does not exist but showuld not be needed
    statusCol.field = "status";
    statusCol.pinned = "left";
    statusCol.lockPosition = true;
    statusCol.headerName = "";
    statusCol.sortable = false;
    statusCol.suppressMovable = true;
    statusCol.resizable = false;
    statusCol.suppressMenu = true;
    statusCol.columnFilterType = ColumnFilterType.NONE;
    statusCol.width = 25;
    statusCol.minWidth = this.oneWs;
    statusCol.maxWidth = 25;
    statusCol.position = this.position.next();
    statusCol.omitFromPicker = true;
    statusCol.disableInPicker = true;
    statusCol.indexType = IndexType.None;

    statusCol.valueGetter = (params) => {
      return params.data?.forwardFirstOrder;
    };

    statusCol.cellRenderer = "orderStatusRenderer";

    return statusCol;
  }

  protected createSelectionColumn() {
    let selectionColumn: BfxColDef = { suppressMenu: true };

    selectionColumn.field = COLUMN_SELECTION;
    selectionColumn.pinned = "left";
    selectionColumn.lockPosition = true;
    selectionColumn.headerName = "";
    selectionColumn.sortable = false;
    selectionColumn.suppressMovable = true;
    selectionColumn.resizable = false;
    selectionColumn.suppressMenu = true;
    selectionColumn.columnFilterType = ColumnFilterType.NONE;
    selectionColumn.width = 53;
    selectionColumn.minWidth = 53;
    selectionColumn.maxWidth = 53;
    selectionColumn.position = this.position.next();
    selectionColumn.omitFromPicker = true;
    selectionColumn.disableInPicker = true;
    selectionColumn.indexType = IndexType.None;
    selectionColumn.cellClass = "status-icon-cell";

    selectionColumn.valueGetter = (params) => {
      let orderPricing = params.data as FwdRollMutableRowModel;
      return (
        orderPricing !== null && orderPricing.supportsSelection && orderPricing.lockedForUserPricing
      );
    };

    selectionColumn.cellRenderer = "selectionRenderer";

    return selectionColumn;
  }

  public getAssetClass(): CubiqTS.AssetClass {
    return CubiqTS.AssetClass.FIX;
  }

  protected getTableCongiPromises(): Array<Promise<any>> {
    return [this._sysConfigService.getSystemConfig(SysFeatures.featuresEnablePrivateComments)];
  }
  // this override to set flags returned by promises
  protected handleTableCongiPromises(values: Array<any>) {
    this.privateCommentsEnabled = this.valueOn(values[0]);
  }

  public formatRate(params: ValueFormatterParams): string {
    if (IS_SPOT_AGGREGATE(params) || IS_HIDDEN(params)) {
      return "";
    }
    if (params.value == null) {
      return "-";
    }

    let value: any = params.value;
    if (typeof params.value == "string") {
      value = new CubiqTS.BigNumber(params.value);
    }

    let minPrecision: number = params.data.minPrecision;
    let currentDecs: number = value.decimalPlaces();
    if (currentDecs < minPrecision) {
      return value.toFixed(minPrecision);
    }

    return value.toFixed(currentDecs);
  }

  public formatPoints(params: ValueFormatterParams): string {
    if (IS_SPOT(params) || IS_HIDDEN(params)) {
      return "";
    }
    if (params.value == null) {
      return "-";
    }
    let value: any = params.value;

    let orderPricing = params.data;
    let pair: CubiqTS.CurrencyPairImpl = this._ccyPairLookupService.getCcyPair(
      orderPricing.baseCcy,
      orderPricing.nonBaseCcy,
    );

    let ptsPrecision;
    if (pair) {
      const inverted = pair.base === orderPricing.nonBaseCcy;

      let decs: number = 4;
      let pips: number = 4;
      if (inverted) {
        decs = Math.max(
          pair.fwdInvertDecimalsQty,
          pair.markupSptInvDecimals,
          pair.limitEntrySpotInvertDecimals,
        );
        pips = pair.fwdInvertPipsQty;
      } else {
        decs = Math.max(
          pair.fwdRateDecimalsQty,
          pair.markupSptDecimals,
          pair.limitEntrySpotDecimals,
        );
        pips = pair.fwdPipsQty;
      }
      ptsPrecision = decs - pips;
    } else {
      logger.out.error(
        "Cannot find currency pair for " + orderPricing.baseCcy + orderPricing.nonBaseCcy,
      );
      ptsPrecision = 2;
    }

    if (!(value instanceof CubiqTS.BigNumber)) {
      try {
        value = new CubiqTS.BigNumber(value);
      } catch (e) {
        logger.out.error("Cannot create points from" + value, e);
        value = new CubiqTS.BigNumber(0);
      }
    }

    return value.toFixed(ptsPrecision);
  }

  protected formatFixedAmt(params: ValueFormatterParams): string {
    const precision = this.getCcyPrecision(params.data?.fixedCcy);
    return this.formatAmt(params, precision);
  }

  protected formatUSDAmt(params: ValueFormatterParams): string {
    const precision = this.getCcyPrecision("USD");
    return this.formatAmt(params, precision);
  }

  protected formatCounterAmt(params: ValueFormatterParams): string {
    let ccyCode;
    if (params.data?.fixedCcy == params.data?.baseCcy) {
      ccyCode = params.data?.nonBaseCcy;
    } else {
      ccyCode = params.data?.baseCcy;
    }
    const precision = this.getCcyPrecision(ccyCode);
    return this.formatAmt(params, precision);
  }

  private getCcyPrecision(_ccyCode: string): number {
    // if (ccyCode) {
    //   const ccy = this._ccyLookupService.getCcy(ccyCode);
    //   if (ccy) {
    //     return ccy.minorUnitsQty.toNumber();
    //   }
    // }
    return 0;
  }
  protected formatAmt(params: ValueFormatterParams, precision: number): string {
    if (params.value) {
      if (params.value instanceof CubiqTS.BigNumber) {
        return params.value.toFormat(precision);
      } else if (typeof params.value == "string") {
        return params.value;
      } else if (typeof params.value == "number") {
        return params.value.toString();
      }
    }
    return "-";
  }

  protected formatSpotMarkup(params: ValueFormatterParams) {
    return this.formatMarkup(params, true);
  }

  protected formatMarkup(params: ValueFormatterParams, spotMarkup: boolean) {
    if (params.data?.spotAggregateFg) {
      return "";
    }
    let value: any = params.value;
    let orderPricing = params.data as FwdRollMutableRowModel;

    // const spotOrder = orderPricing?.settleTenor === CubiqTS.Tenor.SPOT;
    if (orderPricing == null || value == null) {
      return "-";
    }

    let pair: CubiqTS.CurrencyPairImpl = this._ccyPairLookupService.getCcyPair(
      orderPricing.baseCcy,
      orderPricing.nonBaseCcy,
    );

    let minPrecision: number, maxPrecision: number;
    if (pair) {
      const inverted = pair.base === orderPricing.nonBaseCcy;
      if (spotMarkup) {
        minPrecision = inverted ? pair.markupSptDecimals : pair.markupSptInvDecimals;
        maxPrecision = inverted ? pair.markupSptDecimals : pair.markupSptInvDecimals;
      } else {
        minPrecision = inverted ? pair.fwdRateDecimalsQty : pair.fwdInvertDecimalsQty;
        maxPrecision = inverted ? pair.fwdRateDecimalsQty : pair.fwdInvertDecimalsQty;
      }
    } else {
      logger.out.error(
        "Cannot find currency pair for " + orderPricing.baseCcy + orderPricing.nonBaseCcy,
      );
      minPrecision = 0;
      maxPrecision = 0;
    }

    if (!(value instanceof CubiqTS.BigNumber)) {
      try {
        value = new CubiqTS.BigNumber(value);
      } catch (e) {
        logger.out.error("Cannot create points from" + value, e);
        value = new CubiqTS.BigNumber(0);
      }
    }
    // Output to either min precision or man precision (unless the actual precision is somehow greater than max)
    let outPrecision: number;
    const actualPrecision = (value as CubiqTS.BigNumber).decimalPlaces();
    if (actualPrecision <= minPrecision) {
      outPrecision = minPrecision;
    } else {
      outPrecision = Math.max(maxPrecision, actualPrecision);
    }

    return value.toFixed(outPrecision);
  }
}
